<template>
  <q-btn
    class="button-standard"
    :class="buttonClasses"
    :size="size"
    :disable="isDisabled"
    :loading="isLoading"
    :label="isIcon ? null : label"
    :align="align"
    :stack="stack"
    :stretch="stretch"
    :rounded="shape === 'rounded'"
    :flat="isFlat"
    :style="styles"
    :aria-label="label && !isIcon ? null : ($attrs['aria-label'] || label || 'Button with ' + (startIcon || endIcon) + ' icon')"
    no-caps
    no-wrap
    unelevated
    v-bind="{
      ...(startIcon && { icon: startIcon }),
      ...(!isIcon && endIcon && { 'icon-right': endIcon })
    }"
  >
    <div
      v-if="alert"
      class="button-standard--alert"
      :class="alertClasses"
    />
    <template
      v-for="(slot, slotName) in $slots"
      #[slotName]="scope"
      :key="slotName"
    >
      <div
        v-if="alert && isIcon"
        class="button-standard--alert"
        :class="alertClasses"
      />
      <slot
        :name="slotName"
        v-bind="scope || {}"
      />
    </template>
  </q-btn>
</template>

<script setup>
import { computed, useAttrs } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Button' })
const $attrs = useAttrs()
const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validate: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  variant: {
    type: String,
    default: 'solid',
    validator: v => ['solid', 'outline', 'light', 'ghost', 'text'].includes(v)
  },
  color: {
    type: String,
    default: 'secondary',
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert',
      'base',
      'surf',
      'float'
    ].includes(v)
  },
  shape: {
    type: String,
    default: 'rounded',
    validator: v => ['square', 'rounded'].includes(v)
  },
  align: {
    type: String,
    default: 'center',
    validator: v => ['left', 'right', 'center', 'around', 'between', 'evenly'].includes(v)
  },
  alert: {
    type: String,
    default: null,
    validator: v => [
      'primary',
      'secondary',
      'positive',
      'negative',
      'warning',
      'info',
      'accent',
      'invert'
    ].includes(v)
  },
  stack: { type: Boolean, default: false },
  stretch: { type: Boolean, default: false },
  label: { type: String, default: null },
  startIcon: { type: String, default: null },
  endIcon: { type: String, default: null },
  isIcon: { type: Boolean, default: false },
  isDisabled: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  isFullWidth: { type: Boolean, default: false }
})

const isFlat = computed(() => props.variant === 'ghost' || props.variant === 'text')
const buttonClasses = computed(() => cx(
  `variant-${props.variant}`,
  `color-${props.color}`,
  `shape-${props.shape}`,
  `size-${props.size}`,
  props.isIcon && 'is-icon',
  props.startIcon && 'has-start-icon',
  props.endIcon && 'has-end-icon'
))
const alertClasses = computed(() => cx(
  `alert-state-${props.alert}`,
  `alert-size-${props.size}`
))
const styles = computed(() => ({ fontSize: 'initial' }))
</script>

<style scoped>
@reference "#app-styles.css";

.button-standard {
  @apply border font-medium;

  &.q-btn--rectangle:not(.q-btn--rounded) {
    @apply rounded-md;
  }
  .button-standard--alert {
    @apply absolute top-0 right-0 aspect-square rounded-full;

    &.alert-size-xs { @apply w-1.5; }
    &.alert-size-sm { @apply w-1.75; }
    &.alert-size-md { @apply w-2; }
    &.alert-size-lg { @apply w-2.25; }
    &.alert-size-xl { @apply w-2.5; }

    &.alert-state-positive { @apply bg-positive-mid; }
    &.alert-state-negative  { @apply bg-negative-mid; }
    &.alert-state-warning { @apply bg-warning-mid; }
    &.alert-state-info    { @apply bg-info-mid; }
    &.alert-state-accent  { @apply bg-accent-mid; }
    &.alert-state-invert  { @apply layer-invert-base; }
  }
  &.shape-square .button-standard--alert { @apply translate-x-1/3 -translate-y-1/3; }
  &.shape-rounded .button-standard--alert { @apply translate-x-1/5 -translate-y-1/5; }
  &.variant-text .button-standard--alert, &.variant-ghost .button-standard--alert  { @apply translate-x-0 translate-y-0 scale-75; }

  &.size-xs {
    @apply text-[10px]! leading-3 min-h-5 px-2 py-0.75;
    &.has-start-icon { @apply pl-1.25; }
    &.has-end-icon { @apply pr-1.25; }
    &:deep(.q-btn__content) { @apply gap-1; }
    &:deep(.q-icon) { @apply text-[12px] leading-3; }
  }
  &.size-sm {
    @apply text-[12px]! leading-3.5 min-h-6 px-2.25 py-1;
    &.has-start-icon { @apply pl-1.5; }
    &.has-end-icon { @apply pr-1.5; }
    &:deep(.q-btn__content) { @apply gap-1.25; }
    &:deep(.q-icon) { @apply text-[14px] leading-3.5; }
  }
  &.size-md {
    @apply text-[14px]! leading-4 min-h-7 px-2.75 py-1.25;
    &.has-start-icon { @apply pl-2; }
    &.has-end-icon { @apply pr-2; }
    &:deep(.q-btn__content) { @apply gap-1.75; }
    &:deep(.q-icon) { @apply text-[16px] leading-4; }
  }
  &.size-lg {
    @apply text-[16px] leading-4.5 min-h-8 px-3 py-1.5;
    &.has-start-icon { @apply pl-2.25; }
    &.has-end-icon { @apply pr-2.25; }
    &:deep(.q-btn__content) { @apply gap-2; }
    &:deep(.q-icon) { @apply text-[18px]! leading-4.5; }
  }
  &.size-xl {
    @apply text-[18px] leading-4 min-h-9 px-3.5 py-1.75;
    &.has-start-icon { @apply pl-2.5; }
    &.has-end-icon { @apply pr-2.5; }
    &:deep(.q-btn__content) { @apply gap-2.25; }
    &:deep(.q-icon) { @apply text-[20px] leading-4; }
  }

  &.variant-solid {
    &.color-primary   { @apply text-white        bg-primary-mid     edge-primary-mid; }
    &.color-secondary { @apply text-white        bg-secondary-faint edge-secondary-faint; }
    &.color-positive  { @apply text-white        bg-positive-mid    edge-positive-mid; }
    &.color-negative  { @apply text-white        bg-negative-mid    edge-negative-mid; }
    &.color-warning   { @apply text-white        bg-warning-mid     edge-warning-mid; }
    &.color-info      { @apply text-white        bg-info-mid        edge-info-mid; }
    &.color-accent    { @apply text-white        bg-accent-mid      edge-accent-mid; }
    &.color-invert    { @apply ink-invert-strong layer-invert-float edge-invert-float; }
    &.color-base      { @apply ink-soft          layer-base         border-layer-base; }
    &.color-surf      { @apply ink-soft          layer-surf         border-layer-surf; }
    &.color-float     { @apply ink-soft          layer-float        border-layer-float; }
    &.color-overlay   { @apply ink-soft          layer-overlay      border-layer-overlay; }
  }
  &.variant-outline {
    &.color-primary   { @apply text-primary-mid    bg-primary-mid/10        edge-primary-mid; }
    &.color-secondary { @apply text-secondary-deep bg-secondary-deep/10     edge-secondary-deep; }
    &.color-positive  { @apply text-positive-mid   bg-positive-mid/10       edge-positive-mid; }
    &.color-negative  { @apply text-negative-mid   bg-negative-mid/10       edge-negative-mid; }
    &.color-warning   { @apply text-warning-mid    bg-warning-mid/10        edge-warning-mid; }
    &.color-info      { @apply text-info-mid       bg-info-mid/10           edge-info-mid; }
    &.color-accent    { @apply text-accent-mid     bg-accent-mid/10         edge-accent-mid; }
    &.color-invert    { @apply ink-strong          bg-layer-invert-float/10 edge-invert-float; }
    &.color-base      { @apply ink-soft surface-base; }
    &.color-surf      { @apply ink-soft surface-surf; }
    &.color-float     { @apply ink-soft surface-float; }
    &.color-overlay   { @apply ink-soft surface-overlay; }
  }
  &.variant-light {
    &.color-primary   { @apply text-primary-mid    bg-primary-faintest  edge-primary-faintest; }
    &.color-secondary { @apply text-secondary-deep bg-secondary-fainter edge-secondary-fainter; }
    &.color-positive  { @apply text-positive-mid   bg-positive-faintest edge-positive-faintest; }
    &.color-negative  { @apply text-negative-mid   bg-negative-faintest edge-negative-faintest; }
    &.color-warning   { @apply text-warning-mid    bg-warning-faintest  edge-warning-faintest; }
    &.color-info      { @apply text-info-mid       bg-info-faintest     edge-info-faintest; }
    &.color-accent    { @apply text-accent-mid     bg-accent-faintest   edge-accent-faintest; }
    &.color-invert    { @apply ink-strong          bg-secondary-fainter border-secondary-fainter; }
    &.color-base      { @apply ink-soft            layer-base           border-layer-base; }
    &.color-surf      { @apply ink-soft            layer-surf           border-layer-surf; }
    &.color-float     { @apply ink-soft            layer-float          border-layer-float; }
    &.color-overlay   { @apply ink-soft            layer-overlay        border-layer-overlay; }
  }
  &.variant-ghost {
    @apply border-transparent;

    &.color-primary   { @apply text-primary-mid    hover:bg-primary-mid/5       hover:border-primary-mid/5; }
    &.color-secondary { @apply text-secondary-deep hover:bg-secondary-mid/5     hover:border-secondary-mid/5; }
    &.color-positive  { @apply text-positive-mid   hover:bg-positive-mid/5      hover:border-positive-mid/5; }
    &.color-negative  { @apply text-negative-mid   hover:bg-negative-mid/5      hover:border-negative-mid/5; }
    &.color-warning   { @apply text-warning-mid    hover:bg-warning-mid/5       hover:border-warning-mid/5; }
    &.color-info      { @apply text-info-mid       hover:bg-info-mid/5          hover:border-info-mid/5; }
    &.color-accent    { @apply text-accent-mid     hover:bg-accent-mid/5        hover:border-accent-mid/5; }
    &.color-invert    { @apply ink-strong          hover:bg-layer-invert-base/5 hover:border-edge-invert-base/5; }
    &.color-base      { @apply ink-soft            hover:bg-layer-base/5        hover:border-layer-base/5; }
    &.color-surf      { @apply ink-soft            hover:bg-layer-surf/5        hover:border-layer-surf/5; }
    &.color-float     { @apply ink-soft            hover:bg-layer-float/5       hover:border-layer-float/5; }
    &.color-overlay   { @apply ink-soft            hover:bg-layer-overlay/5     hover:border-layer-overlay/5; }
  }
  &.variant-text {
    @apply border-none p-0 min-h-0 rounded-none;
    &.has-start-icon, &.has-end-icon { @apply p-0; }

    &:deep(.q-focus-helper) { @apply hidden; }

    &.color-primary   { @apply text-primary-mid    hover:text-primary-deep; }
    &.color-secondary { @apply text-secondary-deep hover:ink-soft; }
    &.color-positive  { @apply text-positive-mid   hover:text-positive-deep; }
    &.color-negative  { @apply text-negative-mid   hover:text-negative-deep; }
    &.color-warning   { @apply text-warning-mid    hover:text-warning-deep; }
    &.color-info      { @apply text-info-mid       hover:text-info-deep; }
    &.color-accent    { @apply text-accent-mid     hover:text-accent-deep; }
    &.color-invert    { @apply ink-strong          hover:ink-subtle; }
  }

  &.is-icon {
    @apply p-0! aspect-square;
    &.size-xs { @apply w-5; }
    &.size-sm { @apply w-6; }
    &.size-md { @apply w-7; }
    &.size-lg { @apply w-8; }
    &.size-xl { @apply w-9; }
  }

  &:focus-visible {
    @apply outline-2 outline-offset-2 outline-primary-mid;
  }

  &:deep(.q-icon) { @apply m-0!; }
}
</style>
